// Rental charges: automatic drafts from bookings, posted by a person.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileText, Plus, RefreshCw, Trash2 } from "lucide-react";

import { api, formatApiError } from "../../lib/api";
import { CAPABILITIES, can, fmtMinor, idemHeaders } from "../../lib/books";
import { Button, Empty, Field, Loading, Modal, Money, Pill, Section, Table, inputClass } from "./shared";

const LINE_KINDS = [
  { value: "extension", label: "Extension" },
  { value: "delivery", label: "Delivery / pickup" },
  { value: "additional_driver", label: "Additional driver" },
  { value: "other", label: "Other charge" },
  { value: "discount", label: "Discount (reduces the charge)" },
];

export default function ChargeDraftsTab({ status, onChanged }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const canPost = can(status, CAPABILITIES.post);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/charge-drafts");
      setDrafts(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refreshOpen = async (draftId) => {
    const { data } = await api.get(`/finance/charge-drafts/${draftId}`);
    setOpen(data);
    load();
  };

  const post = async (draft) => {
    try {
      const { data } = await api.post(`/finance/charge-drafts/${draft.id}/post`,
                                      { expected_revision: draft.revision }, { headers: idemHeaders() });
      toast.success(`Posted ${data.document.number} (${fmtMinor(data.document.total_minor)}).`);
      setOpen(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const ready = drafts.filter((d) => d.status === "ready_to_post");
  const expected = drafts.filter((d) => d.status === "expected");
  const closed = drafts.filter((d) => ["posted", "voided"].includes(d.status));

  if (loading) return <Loading label="Checking bookings for charges..." />;

  return (
    <div className="space-y-4" data-testid="books-charges">
      <Section
        title="Ready to post"
        description="Completed bookings whose rental charge has not been recorded yet."
        actions={<Button variant="secondary" onClick={load} data-testid="books-drafts-refresh">
                   <RefreshCw className="w-4 h-4" /> Refresh
                 </Button>}
        testId="books-drafts-ready">
        <DraftTable rows={ready} onOpen={setOpen} emptyText="Nothing is waiting to be posted." />
      </Section>

      <Section title="Expected" description="Confirmed bookings that will produce a charge when they complete."
               testId="books-drafts-expected">
        <DraftTable rows={expected} onOpen={setOpen} emptyText="No upcoming charges." />
      </Section>

      {closed.length > 0 && (
        <Section title="Posted and voided" testId="books-drafts-closed">
          <DraftTable rows={closed} onOpen={setOpen} emptyText="" />
        </Section>
      )}

      {open && (
        <DraftModal draft={open} canPost={canPost} onClose={() => setOpen(null)}
                    onRefresh={() => refreshOpen(open.id)} onPost={() => post(open)} />
      )}
    </div>
  );
}

function DraftTable({ rows, onOpen, emptyText }) {
  return (
    <Table head={["Booking", "Service date", { key: "flags", label: "Review" },
                  { key: "amount", label: "Amount", align: "right" }, ""]}
           empty={rows.length === 0 && emptyText ? <Empty>{emptyText}</Empty> : null}>
      {rows.map((draft) => {
        const openFlags = (draft.flags || []).filter((f) => !f.resolved && f.blocking);
        return (
          <tr key={draft.id} data-testid={`books-draft-row-${draft.booking_reference}`}>
            <td className="py-2 pr-3">
              <div className="font-medium text-slate-900">{draft.booking_reference}</div>
              <Pill status={draft.status}>{draft.status.replace(/_/g, " ")}</Pill>
            </td>
            <td className="py-2 pr-3 text-slate-600">{draft.service_date || "-"}</td>
            <td className="py-2 pr-3">
              {openFlags.length > 0
                ? <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                    <AlertTriangle className="w-3.5 h-3.5" /> {openFlags.length} to review
                  </span>
                : <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Clear
                  </span>}
            </td>
            <td className="py-2 pr-3 text-right"><Money minor={draft.net_minor} /></td>
            <td className="py-2 pr-3 text-right">
              <Button variant="secondary" onClick={() => onOpen(draft)}
                      data-testid={`books-draft-open-${draft.booking_reference}`}>
                <FileText className="w-4 h-4" /> Open
              </Button>
            </td>
          </tr>
        );
      })}
    </Table>
  );
}

function DraftModal({ draft, canPost, onClose, onRefresh, onPost }) {
  const [adding, setAdding] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const openFlags = (draft.flags || []).filter((f) => !f.resolved);

  const resolveFlag = async (code) => {
    if (note.trim().length < 5) return toast.error("Say what you checked (at least 5 characters).");
    setBusy(true);
    try {
      await api.post(`/finance/charge-drafts/${draft.id}/flags/${code}/resolve`,
                     { expected_revision: draft.revision, reason: note.trim() });
      setNote("");
      await onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const addLine = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/charge-drafts/${draft.id}/lines`, {
        expected_revision: draft.revision, kind: adding.kind, description: adding.description,
        amount: adding.amount, reason: adding.reason || undefined,
      }, { headers: idemHeaders() });
      setAdding(null);
      await onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeLine = async (line) => {
    const reason = window.prompt("Why is this line removed?");
    if (!reason || reason.trim().length < 5) return;
    try {
      await api.post(`/finance/charge-drafts/${draft.id}/lines/${line.id}/remove`,
                     { expected_revision: draft.revision, reason: reason.trim() });
      await onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const voidDraft = async () => {
    const reason = window.prompt("Why is there nothing to charge?");
    if (!reason || reason.trim().length < 5) return;
    try {
      await api.post(`/finance/charge-drafts/${draft.id}/void`,
                     { expected_revision: draft.revision, reason: reason.trim() });
      toast.success("Draft voided.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Modal title={`Rental charge - booking ${draft.booking_reference}`} onClose={onClose}
           description={draft.service_date ? `Service date ${draft.service_date}` : "Not completed yet"}
           testId="books-draft-modal">
      <Table head={["Line", { key: "amount", label: "Amount", align: "right" }, ""]}>
        {(draft.lines || []).map((line) => (
          <tr key={line.id}>
            <td className="py-2 pr-3">
              <div className="text-slate-900">{line.description}</div>
              <div className="text-xs text-slate-500">
                {line.kind.replace(/_/g, " ")} · {line.source === "manual" ? "added here" : "from the confirmed booking"}
              </div>
            </td>
            <td className="py-2 pr-3 text-right">
              <Money minor={line.kind === "discount" ? -line.amount_minor : line.amount_minor} />
            </td>
            <td className="py-2 pr-3 text-right">
              {line.source === "manual" && draft.status !== "posted" && canPost && (
                <button onClick={() => removeLine(line)} className="text-rose-600 hover:text-rose-700"
                        aria-label="Remove line" data-testid={`books-draft-remove-${line.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </td>
          </tr>
        ))}
        <tr className="font-semibold">
          <td className="py-2 pr-3">Total to charge</td>
          <td className="py-2 pr-3 text-right"><Money minor={draft.net_minor} /></td>
          <td />
        </tr>
      </Table>

      {openFlags.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="books-draft-flags">
          <div className="text-sm font-medium text-amber-900">Check these before posting</div>
          {openFlags.map((flag) => (
            <div key={flag.code} className="text-sm text-slate-700">
              <div className="font-medium">{flag.message}</div>
              {canPost && flag.blocking && (
                <div className="mt-1 flex flex-wrap gap-2">
                  <input className={`${inputClass} sm:w-72`} placeholder="What did you check?"
                         value={note} onChange={(e) => setNote(e.target.value)}
                         data-testid={`books-flag-note-${flag.code}`} />
                  <Button variant="secondary" busy={busy} onClick={() => resolveFlag(flag.code)}
                          data-testid={`books-flag-resolve-${flag.code}`}>
                    Mark checked
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canPost && draft.status !== "posted" && (
        adding ? (
          <div className="rounded-xl border border-slate-200 p-3 grid sm:grid-cols-2 gap-3">
            <Field label="Type">
              <select className={inputClass} value={adding.kind}
                      onChange={(e) => setAdding({ ...adding, kind: e.target.value })}>
                {LINE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </Field>
            <Field label="Amount (₱)">
              <input className={inputClass} inputMode="decimal" value={adding.amount} data-testid="books-line-amount"
                     onChange={(e) => setAdding({ ...adding, amount: e.target.value })} />
            </Field>
            <Field label="Description" className="sm:col-span-2">
              <input className={inputClass} value={adding.description} data-testid="books-line-description"
                     onChange={(e) => setAdding({ ...adding, description: e.target.value })} />
            </Field>
            {adding.kind === "discount" && (
              <Field label="Reason for the discount" className="sm:col-span-2">
                <input className={inputClass} value={adding.reason || ""}
                       onChange={(e) => setAdding({ ...adding, reason: e.target.value })} />
              </Field>
            )}
            <div className="sm:col-span-2 flex gap-2">
              <Button onClick={addLine} busy={busy} data-testid="books-line-save">Add line</Button>
              <Button variant="secondary" onClick={() => setAdding(null)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" data-testid="books-draft-add-line"
                  onClick={() => setAdding({ kind: "extension", amount: "", description: "" })}>
            <Plus className="w-4 h-4" /> Add a line
          </Button>
        )
      )}

      {canPost && draft.status === "ready_to_post" && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
          <Button onClick={onPost} data-testid="books-draft-post">Post this charge</Button>
          <Button variant="danger" onClick={voidDraft} data-testid="books-draft-void">Nothing to charge</Button>
        </div>
      )}
      {draft.status === "posted" && (
        <p className="text-sm text-emerald-700">Posted. Corrections are made with a reversal or a credit note.</p>
      )}
    </Modal>
  );
}
